import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  Link,
  Container,
  IconButton,
  Image
} from "@chakra-ui/react";
import { Menu, UserRound } from "lucide-react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";

import DataListView from "./DataListView";
import logo from './assets/eumetsat_40.svg';
import CreateOrderView from "./CreateOrderView";
import LoginView from "./LoginView";
import AuthService from "./AuthService";
import { GeneralDisclaimerBanner } from "./Disclaimers";

function ProtectedRoute({ children }) {
  return AuthService.isAuthenticated() ? children : <Navigate to="/login" replace />;
}

const EumetsatHeader = ({ onResetCreateOrder, onNavigateToOrders, onLogout }) => {
  return (
    <Box 
      bg="#00205B"
      color="white" 
      py={0} 
      px={{ base: 4, md: 8 }} 
      boxShadow="md" 
      position="sticky" 
      top={0} 
      zIndex={100}
    >
      <Flex maxW="100vw" mx="auto" align="center" justify="space-between">
        
        {/* Left Side: Logo and Title */}
        <HStack gap={0} marginEnd="auto">
          <HStack gap={0} cursor="pointer" userSelect="none" height="69px">
            <Image src={logo} height="77px"></Image>
          </HStack>
          
          <Text fontSize="xl" fontWeight="700" color="gray.200" display={{ base: "none", md: "block" }}>
            LTA Data Ordering
          </Text>
        </HStack>
        

        {/* Middle: Main Navigation (Hidden on mobile) */}
        <HStack gap={6} display={{ base: "none", lg: "flex" }} ml={8}>
          <NavLink to="/create-order" onClick={onResetCreateOrder}>
            {({ isActive }) => (
              <Box
                as="span"
                color={isActive ? "blue.300" : "white"}
                fontWeight={isActive ? "bold" : "medium"}
                borderBottom={isActive ? "2px solid" : "2px solid transparent"}
                borderColor="blue.300"
                pb={1}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ color: "blue.300" }}
              >
                Create new order
              </Box>
            )}
          </NavLink>

          {/* My Orders Link */}
          <NavLink to="/orders" onClick={onNavigateToOrders}>
            {({ isActive }) => (
              <Box
                as="span"
                color={isActive ? "blue.300" : "white"}
                fontWeight={isActive ? "bold" : "medium"}
                borderBottom={isActive ? "2px solid" : "2px solid transparent"}
                borderColor="blue.300"
                pb={1}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ color: "blue.300" }}
              >
                My orders
              </Box>
            )}
          </NavLink>
        </HStack>

        <Box flex={1} />

        {/* Right Side: Search, Cart, Account Actions */}
        <HStack gap={4}>
          
          <Link href="#" color="white" fontWeight="200" _hover={{ color: "blue.300", textDecoration: "none" }}>&lt; back to Data Store</Link>
          <HStack>
            <UserRound size={28} style={{ marginRight: "8px" }} />
            {AuthService.getUsername()}
          </HStack>
          <Button
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={onLogout}
          >
            Sign out
          </Button>

          {/* Mobile burger menu */}
          <IconButton 
            aria-label="Menu" 
            variant="ghost" 
            color="white" 
            display={{ base: "flex", lg: "none" }}
            _hover={{ bg: "whiteAlpha.200" }}
          >
            <Menu size={24} />
          </IconButton>
        </HStack>
      </Flex>
    </Box>
  );
};

const ContentArea = ({ createOrderKey }) => {
  return (
    <Box bg="gray.50" minH="calc(100vh - 64px)" p={{ base: 4, md: 8 }}>
      <Container maxW="1400px">
        <Routes>
          <Route path="/login" element={<LoginView />} />
          <Route path="/" element={<Navigate to="/orders" replace />} />
          <Route path="/orders" element={<ProtectedRoute><DataListView /></ProtectedRoute>} />
          <Route path="/create-order" element={<ProtectedRoute><CreateOrderView key={createOrderKey} /></ProtectedRoute>} />
        </Routes>
      </Container>
    </Box>
  );
};

function AppContent() {
  const [createOrderKey, setCreateOrderKey] = useState(0);
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isResetting && location.pathname === '/create-order' && location.search === '') {
      setCreateOrderKey(prev => prev + 1);
      setIsResetting(false);
    }
  }, [location, isResetting]);

  // Re-arm proactive JWT refresh after a page reload (the timer lives in
  // memory, but the token + api_key persist in localStorage).
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      AuthService.scheduleRefresh();
    }
  }, []);
  
  const handleResetCreateOrder = (e) => {
    e.preventDefault();
    setIsResetting(true);
    navigate('/create-order', { replace: true });
  };

  const handleLogout = () => {
    AuthService.logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box>
      <EumetsatHeader onResetCreateOrder={handleResetCreateOrder} onNavigateToOrders={() => {}} onLogout={handleLogout} />
      <GeneralDisclaimerBanner />
      <ContentArea createOrderKey={createOrderKey} />
    </Box>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
      <AppContent />
    </BrowserRouter>
  );
}